import React from "react";
import { Box, Typography, Card, Button, Avatar, Grid } from "@mui/material";
import { FormattedMessage } from "react-intl";
import useMediaQuery from "@mui/material/useMediaQuery";
import { makeStyles } from "@mui/styles";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Icon1 from "../../../../../../app/assets/images/icons/user.svg";
import Icon2 from "../../../../../../app/assets/images/icons/smile_circle.svg";
import Icon4 from "../../../../../../app/assets/images/icons/text_files.svg";
import DefaultIcon from "../../../../../../app/assets/images/icons/Group 1238.svg";
import DefaultAvatar from "../../../../../../app/assets/images/pictures/square-femme.jpg";
import theme from "../../../../Layouts/theme";

const MyCandidatesStats = ({ kanban, kanbanSummary }) => {
  const i18nLocale = useSelector((state) => state.i18nLocale);
  const navigate = useNavigate();
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up("md"));

  const kanbanColumns = Array.isArray(kanban?.columns) ? kanban.columns : [];
  const summaryColumns = Array.isArray(kanbanSummary?.columns) ? kanbanSummary.columns : [];
  const columns = kanbanColumns.length > 0 ? kanbanColumns : summaryColumns;

  const cardsByColumnId = kanban?.cards_by_column_id || {};
  
  const kanbanTopCandidates = kanban?.top_candidates_by_column_id || {};
  const summaryTopCandidates = kanbanSummary?.top_candidates_by_column_id || {};
  const topCandidatesByColumnId = Object.keys(kanbanTopCandidates).length > 0 
    ? kanbanTopCandidates 
    : summaryTopCandidates;

  const kanbanColumnCounts = kanban?.column_counts_by_id || {};
  const summaryColumnCounts = kanbanSummary?.column_counts_by_id || {};
  const columnCountsById = Object.keys(kanbanColumnCounts).length > 0 
    ? kanbanColumnCounts 
    : summaryColumnCounts;

  const orderedColumns = [...columns].sort((a, b) => (a.position || 0) - (b.position || 0));

  const iconForColumn = (col) => {
    if (col?.key === "pending") return Icon1;
    if (col?.key === "hired") return Icon4;
    return Icon2;
  };

  const cardsForColumn = (colId) => {
    const cards = cardsByColumnId?.[String(colId)];
    return Array.isArray(cards) ? cards : [];
  };

  const topCandidatesForColumn = (colId) => {
    const list = topCandidatesByColumnId?.[String(colId)];
    return Array.isArray(list) ? list : [];
  };

  const countForColumn = (colId) => {
    const cards = cardsForColumn(colId);
    if (cards.length > 0) return cards.length;

    const count = columnCountsById?.[String(colId)];
    return Number.isFinite(Number(count)) ? Number(count) : 0;
  };


  const useStyles = makeStyles(() => ({
    candidateText: {
      fontWeight: "500",
      fontFamily: "Segoe UI Semibold, sans-serif",
      color: "#1B1B1B",
      marginBottom: 0,
      marginRight: isDesktop ? 0 : "4px",
      width: isDesktop ? "auto" : "90px",
      fontSize: "14px",
    },
    cardContainer: {
      padding: "12px",
      display: "flex",
      justifyContent: "flex-start",
      alignItems: isDesktop ? "flex-start" : "center",
      flexDirection: isDesktop ? "column" : "row",
      height: "100%",
      borderRadius: "12px",
      border: `1px solid ${theme.palette.background.neutral}`,
      boxShadow: "none",
      flex: 1,
      minHeight: isDesktop ? "150px" : "auto",
    },
    cardTopRow: {
      display: "flex", 
      justifyContent: "flex-start", 
      alignItems: "center",
      marginBottom: isDesktop ? "8px" : "0", 
      paddingBottom: isDesktop ? "8px" : "0",
    },
    boardButton: {
      borderRadius: "999px",
      height: "30px",
      color: `${theme.palette.text.main} !important`, 
      border: `1px solid ${theme.palette.secondary.grey_strong} !important`,
      minWidth: "auto",
      padding: "6px 24px",
      fontSize: isDesktop ? "inherit" : "0.7rem",
      backgroundColor: "#FFF",
      textTransform: "none",
      fontWeight: "600 !important",
      fontFamily: "Segoe UI Semibold, sans-serif",
      marginTop: isDesktop ? "auto !important" : "0",
      "&:hover": {
        backgroundColor: theme.palette.secondary?.hover,
      },
    },
  }));

  const classes = useStyles();

  return(
    <Box sx={{ flex: 1, display: "flex" }}>
      <Card sx={{p: "20px", borderRadius: "12px", boxShadow: "none", flex: 1, gap: "20px", border: `1px solid ${theme.palette.background.neutral}` }}>
        <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
          <Typography variant={isDesktop ? "h5" : "h3"} sx={{fontFamily: "Segoe UI Semibold, sans-serif", fontSize: "20px !important", color: theme.palette.text.main }}>
            <b><FormattedMessage id="recruiter.overview.my_candidates" /></b>
          </Typography>
          
        </Box>

        <Grid container spacing={1} sx={{ mt: "20px" }} alignItems="stretch">
          {orderedColumns.map((col) => {
            const cards = cardsForColumn(col.id);
            const count = countForColumn(col.id);

            const candidatesFromCards = cards.slice(0, 3).map((c) => c.candidate).filter(Boolean);
            const candidatesFromSummary = topCandidatesForColumn(col.id);
            const avatars = (candidatesFromCards.length > 0 ? candidatesFromCards : candidatesFromSummary).slice(0, 3);

            const hasMore = count > 3;
            const avatarSrc = (url) => (url && url !== "null" ? url : DefaultAvatar);


            return (
              <Grid key={col.id} item xs={12} sm={6} md={4} sx={{ display: "flex" }}>
                <Card className={classes.cardContainer}>
                  <Box className={classes.cardTopRow}>
                    <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
                      <Avatar src={iconForColumn(col)} alt={col.name} sx={{ width: 24, height: 24, mr: "12px", borderRadius: "0" }} />
                      <Typography
                        variant={isDesktop ? "h6" : "h3"}
                        className={classes.candidateText}
                        sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                        gutterBottom
                        title={col.name}
                      >
                        {col.name}
                      </Typography>
                    </Box>

                    {!isDesktop ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <Typography variant="h1" sx={{fontWeight: "bold", fontFamily: "Segoe UI Bold, sans-serif", fontSize: "24px !important" }}>
                          {count}
                        </Typography>
                        <Box sx={{display: "flex", gap: "-12px" }}>
                          {avatars.slice(0, 3).map((c, index) => (
                            <Avatar key={c.id} src={avatarSrc(c.profile_picture_url)} alt={c.full_name || "Candidate"} sx={{ width: 24, height: 24, ml: index === 0 ? "8px" : "-12px", borderRadius: "50%", border: "0.5px solid #FFF" }} />
                          ))}
                          {hasMore ? (
                            <Avatar src={DefaultIcon} sx={{ width: 24, height: 24, ml: "-12px", borderRadius: "50%", border: "0.5px solid #FFF" }} />
                          ) : null}
                        </Box>
                      </Box>
                    ) : null}
                  </Box>

                  {isDesktop ? (
                    <>
                      {col.key === "pending" ? (
                        <Typography color="text.secondary" sx={{ fontSize: "12px", color: theme.palette.secondary.black, fontWeight: 400, fontFamily: "Segoe UI Regular, sans-serif", lineHeight: "12px", mb: "24px" }}>
                          <FormattedMessage id="recruiter.overview.pending_description" />
                        </Typography>
                      ) : col.key === "hired" ? (
                        <Typography color="text.secondary" sx={{ fontSize: "12px", color: theme.palette.secondary.black, fontWeight: 400, fontFamily: "Segoe UI Regular, sans-serif", lineHeight: "12px", mb: "24px" }}>
                          <FormattedMessage id="recruiter.overview.recruited_description" />
                        </Typography>
                      ) : (
                        <Typography color="text.secondary" sx={{ fontSize: "12px", color: theme.palette.secondary.black, fontWeight: 400, fontFamily: "Segoe UI Regular, sans-serif", lineHeight: "12px", mb: "24px" }}>
                          <FormattedMessage id="recruiter.overview.placeholder_description" />
                        </Typography>
                      )}
                      <Box sx={{ width: "100%", height: "0px", borderBottom: "2px solid #E0E0E0", borderColor: theme.palette.background.neutral, mt: "auto" }} />
                    </>
                  ) : null}

                  <Box sx={{display: "flex", justifyContent: "space-between", width: "100%", mt: isDesktop ? "12px" : "0" }}>
                    <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
                      {isDesktop ? (
                        <>
                          <Typography variant="h3" sx={{ fontWeight: "bold", fontFamily: "Segoe UI Bold, sans-serif", fontSize: "2.125rem" }}>
                            {count}
                          </Typography>
                          <Typography variant="body2" sx={{ ml: "8px", fontWeight: 600, fontFamily: "Segoe UI Semibold, sans-serif" }}>
                            <FormattedMessage id="menu.candidates" />
                          </Typography>
                          <Box sx={{display: "flex", gap: "-12px", ml: "12px" }}>
                            {avatars.map((c, index) => (
                              <Avatar key={c.id} src={avatarSrc(c.profile_picture_url)} alt={c.full_name || "Candidate"} sx={{ width: 24, height: 24, ml: index === 0 ? "8px" : "-12px", borderRadius: "50%", border: "0.5px solid #FFF" }} />
                            ))}
                            
                            {hasMore ? (
                              <Avatar src={DefaultIcon} sx={{ width: 24, height: 24, ml: "-12px", borderRadius: "50%" }} />
                            ) : null}
                          </Box>
                        </>
                      ) : null}
                    </Box>

                    <Box onClick={() => navigate(`/${i18nLocale}/recruiter?tab=applications`)} sx={{ cursor: "pointer", display: "flex", alignItems: "center" }}>
                      {col.key === "pending" ? (
                        <svg xmlns="http://www.w3.org/2000/svg" width="34" height="24" viewBox="0 0 35 24" fill="none">
                          <rect width="34.2021" height="23.4062" rx="11.7031" fill={theme.palette.secondary?.hover} />
                          <path d="M10.2109 11.452H22.7767M22.7767 11.452L17.1807 17.048M22.7767 11.452L17.1807 6.3584" stroke="#222222"/>
                        </svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" width="35" height="24" viewBox="0 0 35 24" fill="none">
                          <rect width="34.2021" height="23.4062" rx="11.7031" fill="#023331"/>
                          <path d="M10.2109 11.452H22.7767M22.7767 11.452L17.1807 17.048M22.7767 11.452L17.1807 6.3584" stroke="white"/>
                        </svg>
                      )}
                    </Box>
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>
        <Box sx={{mt: "20px"}}>
          <Button variant="outlined" onClick={() => navigate(`/${i18nLocale}/recruiter?tab=applications`)} className={classes.boardButton}>
            <FormattedMessage id="recruiter.overview.all_candidates" />
          </Button>
        </Box>
      </Card>
    </Box>
  );
};

export default MyCandidatesStats;